import { useState, useCallback } from 'react';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { CapturedPhoto } from '../types/submission-workflow';
import { ImageCompressionService } from '../services/imageCompressionService';

interface UploadProgress {
  [photoId: string]: number;
}

interface UploadError {
  [photoId: string]: string;
}

export interface PhotoUploadResult {
  success: boolean;
  photo?: CapturedPhoto;
  error?: string;
}

export const usePhotoUpload = () => {
  const { user } = useFirebase();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [uploadErrors, setUploadErrors] = useState<UploadError>({});
  const [isUploading, setIsUploading] = useState(false);

  const uploadPhoto = useCallback(async (photo: CapturedPhoto): Promise<PhotoUploadResult> => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      setIsUploading(true);
      setUploadProgress(prev => ({ ...prev, [photo.id]: 0 }));
      setUploadErrors(prev => {
        const updated = { ...prev };
        delete updated[photo.id];
        return updated;
      });

      // Convert blob URL to File/Blob
      const response = await fetch(photo.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch blob: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      setUploadProgress(prev => ({ ...prev, [photo.id]: 10 }));

      // Create File object from blob for compression
      const file = new File([blob], photo.filename, { type: blob.type });
      setUploadProgress(prev => ({ ...prev, [photo.id]: 20 }));

      // Compress the image before upload
      console.log(`Compressing photo ${photo.filename} before upload...`);
      const compressionResult = await ImageCompressionService.compressImage(file, {
        maxWidthOrHeight: 1920,
        maxSizeMB: 0.8, // 800KB
        quality: 0.85,
        fileType: 'jpeg',
        preserveExif: true
      });

      const fileToUpload = compressionResult.success ? compressionResult.file : file;
      
      if (compressionResult.success) {
        console.log(`Compressed ${photo.filename}: ${compressionResult.originalSize} → ${compressionResult.compressedSize} bytes (${Math.round(compressionResult.compressionRatio * 100)}% reduction)`);
      } else {
        console.warn(`Compression failed for ${photo.filename}, using original:`, compressionResult.error);
      }

      setUploadProgress(prev => ({ ...prev, [photo.id]: 30 }));

      // Import Firebase Storage functions
      const { storage } = await import('../lib/firebase/config');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

      // Generate unique file path for workflow photos
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const fileExtension = photo.filename.split('.').pop() || 'jpg';
      const fileName = `${timestamp}_${randomId}.${fileExtension}`;
      const filePath = `workflow_photos/${user.uid}/${fileName}`;

      setUploadProgress(prev => ({ ...prev, [photo.id]: 40 }));

      // Create storage reference and upload compressed file
      const storageRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(storageRef, fileToUpload);
      
      setUploadProgress(prev => ({ ...prev, [photo.id]: 80 }));

      // Get download URL
      const downloadUrl = await getDownloadURL(uploadResult.ref);
      
      setUploadProgress(prev => ({ ...prev, [photo.id]: 100 }));

      // Create updated photo object with Firebase Storage URL
      const uploadedPhoto: CapturedPhoto = {
        ...photo,
        url: downloadUrl,
        // Store original blob URL for cleanup
        originalBlobUrl: photo.url
      };

      // Clean up blob URL
      URL.revokeObjectURL(photo.url);

      // Clear progress after delay
      setTimeout(() => {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[photo.id];
          return updated;
        });
      }, 2000);

      return { success: true, photo: uploadedPhoto };

    } catch (error) {
      console.error('Error uploading photo:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadErrors(prev => ({ ...prev, [photo.id]: errorMessage }));
      
      // Clear progress
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[photo.id];
        return updated;
      });

      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false);
    }
  }, [user]);

  const uploadMultiplePhotos = useCallback(async (photos: CapturedPhoto[]): Promise<PhotoUploadResult[]> => {
    const results: PhotoUploadResult[] = [];
    
    // Upload photos sequentially to avoid overwhelming the system
    for (const photo of photos) {
      const result = await uploadPhoto(photo);
      results.push(result);
    }
    
    return results;
  }, [uploadPhoto]);

  const clearProgress = useCallback((photoId: string) => {
    setUploadProgress(prev => {
      const updated = { ...prev };
      delete updated[photoId];
      return updated;
    });
  }, []);

  const clearError = useCallback((photoId: string) => {
    setUploadErrors(prev => {
      const updated = { ...prev };
      delete updated[photoId];
      return updated;
    });
  }, []);

  return {
    uploadPhoto,
    uploadMultiplePhotos,
    uploadProgress,
    uploadErrors,
    isUploading,
    clearProgress,
    clearError
  };
};